import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { User, Globe2, Mail, Lock, Save, Trash2, AlertTriangle } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { countries } from '../utils/countries';
import './Dashboard.css';

function Settings({ user, onLogout, onUpdateUser }) {
  const [fullName, setFullName] = useState(user.fullName || '');
  const [lang, setLang] = useState(user.language || 'en');
  const [country, setCountry] = useState(user.country || 'TR');
  const [emailNotifications, setEmailNotifications] = useState(user.emailNotifications !== false);
  const [emailCc, setEmailCc] = useState(user.emailCc || '');
  const [passwords, setPasswords] = useState({ old: '', new: '', confirm_password: '' });
  const [profileMessage, setProfileMessage] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  
  // ✅ Hesap Silme State'leri
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [deleteMessage, setDeleteMessage] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  const { t } = useTranslation();

  const handleUpdateProfile = async () => {
    setProfileMessage('');
    try {
      await axios.patch('/auth/profile', { 
        fullName, 
        language: lang, 
        country
      });
      const res = await axios.get('/auth/verify');
      const updatedUser = res.data.user;
      onUpdateUser(updatedUser);
      setProfileMessage(t('success.profileUpdated'));
    } catch (err) {
      const errorCode = err.response?.data?.error_code;
      setProfileMessage(errorCode ? t(`error.${errorCode}`) : t('error.updateFailed'));
    }
  };

  const handleUpdateEmailSettings = async () => {
    setEmailMessage('');
    try {
      await axios.patch('/auth/profile', { 
        emailNotifications,
        emailCc: emailCc.trim() || null
      });
      const res = await axios.get('/auth/verify');
      const updatedUser = res.data.user;
      onUpdateUser(updatedUser);
      setEmailMessage(t('success.profileUpdated'));
    } catch (err) {
      const errorCode = err.response?.data?.error_code;
      setEmailMessage(errorCode ? t(`error.${errorCode}`) : t('error.updateFailed'));
    }
  };

  const handleChangePassword = async () => {
    setPasswordMessage('');
    if (passwords.new !== passwords.confirm_password) {
      setPasswordMessage(t('error.passwordMismatch'));
      return;
    }
    if (passwords.new.length < 6) {
      setPasswordMessage(t('error.passwordTooShort'));
      return;
    }
    try {
      await axios.post('/auth/change-password', {
        oldPassword: passwords.old,
        newPassword: passwords.new,
        confirm_password: passwords.confirm_password
      });
      setPasswordMessage(t('success.passwordChanged'));
      setPasswords({ old: '', new: '', confirm_password: '' });
    } catch (err) {
      setPasswordMessage(err.response?.data?.error || t('error.passwordChangeFailed'));
    }
  };

  // ✅ Hesap Silme Fonksiyonu
  const handleDeleteAccount = async () => {
    setDeleteMessage('');
    
    // Onay kontrolü
    if (confirmText !== 'DELETE') {
      setDeleteMessage(lang === 'tr' 
        ? 'Lütfen onaylamak için "DELETE" yazın' 
        : 'Please type "DELETE" to confirm');
      return;
    }

    // Son onay
    const finalConfirm = window.confirm(
      lang === 'tr'
        ? '⚠️ SON UYARI: Bu işlem GERİ ALINAMAZ!\n\n' +
          'Hesabınız ve tüm verileriniz kalıcı olarak silinecek:\n' +
          '• Tüm alan adları\n' +
          '• Kontrol geçmişi\n' +
          '• Ekran görüntüleri\n' +
          '• Abonelik kayıtları\n\n' +
          'İADE YAPILMAYACAKTIR.\n\n' +
          'Devam etmek istediğinize emin misiniz?'
        : '⚠️ FINAL WARNING: This action is IRREVERSIBLE!\n\n' +
          'Your account and all data will be permanently deleted:\n' +
          '• All domains\n' +
          '• Check history\n' +
          '• Screenshots\n' +
          '• Subscription records\n\n' +
          'NO REFUNDS WILL BE ISSUED.\n\n' +
          'Are you sure you want to proceed?'
    );

    if (!finalConfirm) return;

    setIsDeleting(true);

    try {
      await axios.delete('/auth/account', {
        data: {
          confirmDeletion: true,
          confirmText: 'DELETE',
          reason: deleteReason || (lang === 'tr' 
            ? 'Kullanıcı hesap silme talebinde bulundu' 
            : 'User requested account deletion')
        }
      });

      // Başarılı silme - Ana sayfaya yönlendir
      alert(lang === 'tr'
        ? 'Hesabınız başarıyla silindi. Ana sayfaya yönlendiriliyorsunuz...'
        : 'Your account has been successfully deleted. Redirecting to home page...');
      
      window.location.href = '/';
      
    } catch (err) {
      const errorCode = err.response?.data?.error_code;
      setDeleteMessage(
        errorCode === 'DELETION_NOT_CONFIRMED' 
          ? (lang === 'tr' ? 'Silme işlemi onaylanmadı' : 'Deletion not confirmed')
        : errorCode === 'CONFIRMATION_TEXT_INCORRECT'
          ? (lang === 'tr' ? 'Lütfen "DELETE" yazın' : 'Please type "DELETE"')
        : (lang === 'tr' ? 'Hesap silme işlemi başarısız oldu' : 'Account deletion failed')
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="dashboard-main">
        <header className="dashboard-header">
          <h1>{t('settings.profile')}</h1>
        </header>

        {/* Profil Ayarları */}
        <div className="section">
          <div className="fx-column form-group">
            <h3>{t('settings.profile')}</h3>
            
            <div className="settings-group">
            <label htmlFor="fullName">{t('auth.fullName')}</label>
            <input
              id="fullName"
              type="text"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder={t('auth.fullName')}
              className="form-input"
            />
            </div>

            <div className="settings-group">
            <label htmlFor="language">{t('settings.language')}</label>
            <select
              id="language"
              value={lang}
              onChange={e => setLang(e.target.value)}
              className="form-select"
            >
              <option value="tr">Türkçe</option>
              <option value="en">English</option>
            </select>
            </div>

            <div className="settings-group">
            <label htmlFor="country">{t('settings.country')}({t('settings.forDateTime')})</label>
            <select
              id="country"
              value={country}
              onChange={e => setCountry(e.target.value)}
              className="form-select"
            >
              {countries.map(c => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
            </div>
            
            <button onClick={handleUpdateProfile} className="btn-primary">
              <Save size={16} strokeWidth={2} style={{marginRight: '6px', verticalAlign: 'middle'}} />
              {t('action.update')}
            </button>
            
            {profileMessage && (
              <div className={`alert ${profileMessage.includes('başarı') || profileMessage.includes('success') ? 'alert-success' : 'alert-error'}`}>
                {profileMessage}
              </div>
            )}
          </div>
        </div>

        {/* Email Bildirimleri */}
        <div className="section">
          <div className="fx-column form-group">
            <h3>{t('settings.emailNotifications')}</h3>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
              <input
                id="emailNotifications"
                type="checkbox"
                checked={emailNotifications}
                onChange={e => setEmailNotifications(e.target.checked)}
                style={{ width: 'auto', cursor: 'pointer' }}
              />
              <label htmlFor="emailNotifications" style={{ margin: 0, cursor: 'pointer' }}>
                {t('settings.enableEmailNotifications')}
              </label>
            </div>

            {emailNotifications && (
              <>
                <label htmlFor="emailCc">{t('settings.emailCc')}</label>
                <input
                  id="emailCc"
                  type="email"
                  value={emailCc}
                  onChange={e => setEmailCc(e.target.value)}
                  placeholder="cc@example.com"
                  className="form-input"
                />
                <small style={{ color: '#666', fontSize: '12px', marginTop: '-10px' }}>
                  {t('settings.emailCcHelp')}
                </small>
              </>
            )}
            
            <button onClick={handleUpdateEmailSettings} className="btn-primary">
              <Mail size={16} strokeWidth={2} style={{marginRight: '6px', verticalAlign: 'middle'}} />
              {t('action.update')}
            </button>

            {emailMessage && (
              <div className={`alert ${emailMessage.includes('başarı') || emailMessage.includes('success') ? 'alert-success' : 'alert-error'}`}>
                {emailMessage}
              </div>
            )}
          </div>
        </div>

        {/* Şifre Değiştirme */}
        <div className="section">
          <div className="fx-column form-group">
            <h3>{t('settings.changePassword')}</h3>
            <input
              type="password"
              placeholder={t('auth.oldPassword')}
              value={passwords.old}
              onChange={e => setPasswords({...passwords, old: e.target.value})}
              className="form-input"
            />
            <input
              type="password"
              placeholder={t('auth.newPassword')}
              value={passwords.new}
              onChange={e => setPasswords({...passwords, new: e.target.value})}
              className="form-input"
            />
            <input
              type="password"
              placeholder={t('auth.confirmPassword')}
              value={passwords.confirm_password}
              onChange={e => setPasswords({...passwords, confirm_password: e.target.value})}
              className="form-input"
            />
            <button onClick={handleChangePassword} className="btn-danger">
              <Lock size={16} strokeWidth={2} style={{marginRight: '6px', verticalAlign: 'middle'}} />
              {t('action.updatePassword')}
            </button>
            {passwordMessage && (
              <div className={`alert ${passwordMessage.includes('başarı') || passwordMessage.includes('success') ? 'alert-success' : 'alert-error'}`}>
                {passwordMessage}
              </div>
            )}
          </div>
        </div>

        {/* ✅ Hesap Silme Bölümü */}
        <div className="section" style={{ borderTop: '2px solid #ff4444', paddingTop: '30px' }}>
          <div className="fx-column form-group">
            <div style={{ 
              background: '#fff3f3', 
              border: '1px solid #ffcccc', 
              borderRadius: '8px', 
              padding: '20px',
              marginBottom: '20px'
            }}>
              <h4 style={{ color: '#cc0000', marginTop: 0 }}>
                {lang === 'tr' ? 'Hesabı Kalıcı Olarak Sil' : 'Permanently Delete Account'}
              </h4>
              <p style={{ color: '#666', marginBottom: '15px' }}>
                {lang === 'tr' 
                  ? 'Hesabınızı sildiğinizde, kişisel verileriniz kalıcı olarak silinecektir. Bu işlem geri alınamaz.'
                  : 'When you delete your account, your personal data will be permanently deleted. This action cannot be undone.'}
              </p>
              
              <p style={{ color: '#333', fontWeight: 600, marginBottom: '8px' }}>
                {lang === 'tr' ? '🗑️ Silinecek Veriler:' : '🗑️ Data to be Deleted:'}
              </p>
              <ul style={{ color: '#666', marginBottom: '15px' }}>
                <li>{lang === 'tr' ? 'Tüm alan adlarınız' : 'All your domains'}</li>
                <li>{lang === 'tr' ? 'Kontrol geçmişiniz (7 gün)' : 'Your check history (7 days)'}</li>
                <li>{lang === 'tr' ? 'Ekran görüntüleriniz' : 'Your screenshots'}</li>
                <li>{lang === 'tr' ? 'Kişisel bilgileriniz (ad, email vb.)' : 'Your personal information (name, email, etc.)'}</li>
              </ul>
              
              <p style={{ color: '#333', fontWeight: 600, marginBottom: '8px' }}>
                {lang === 'tr' ? '📋 Korunan Veriler (Yasal Zorunluluk):' : '📋 Retained Data (Legal Requirement):'}
              </p>
              <ul style={{ color: '#666', marginBottom: '15px', fontSize: '0.9rem' }}>
                <li>{lang === 'tr' ? 'Fatura kayıtları (5 yıl - VUK Madde 253)' : 'Invoice records (5 years - Tax Law Article 253)'}</li>
                <li>{lang === 'tr' ? 'Hesap silme logları (5 yıl - KVKK/GDPR)' : 'Account deletion logs (5 years - KVKK/GDPR)'}</li>
                <li>{lang === 'tr' ? 'Trial kullanım kayıtları (kötüye kullanım önlemi)' : 'Trial usage records (abuse prevention)'}</li>
              </ul>
              
              <p style={{ color: '#cc0000', fontWeight: 'bold', marginTop: '15px' }}>
                {lang === 'tr' 
                  ? 'Önemli: Yapılmış ödemeler için iade YAPILMAZ.'
                  : 'Important: NO REFUNDS will be issued for past payments.'}
              </p>
            </div>

            <button 
              onClick={() => setShowDeleteModal(true)}
              className="btn-danger"
              style={{ maxWidth: '300px' }}
            >
              <Trash2 size={16} strokeWidth={2} style={{marginRight: '6px', verticalAlign: 'middle'}} />
              {lang === 'tr' ? 'Hesabımı Sil' : 'Delete My Account'}
            </button>
          </div>
        </div>

        {/* ✅ Hesap Silme Modal */}
        {showDeleteModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <h2 style={{ color: '#cc0000', marginTop: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AlertTriangle size={28} />
                {lang === 'tr' ? 'Hesabı Sil' : 'Delete Account'}
              </h2>
              
              <p style={{ marginBottom: '20px', color: '#666' }}>
                {lang === 'tr'
                  ? 'Bu işlem geri alınamaz. Hesabınız ve tüm verileriniz kalıcı olarak silinecektir.'
                  : 'This action cannot be undone. Your account and all data will be permanently deleted.'}
              </p>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="deleteReason">
                  {lang === 'tr' ? 'Silme Nedeni (isteğe bağlı)' : 'Reason for Deletion (optional)'}
                </label>
                <textarea
                  id="deleteReason"
                  value={deleteReason}
                  onChange={e => setDeleteReason(e.target.value)}
                  placeholder={lang === 'tr' 
                    ? 'Hesabınızı neden silmek istiyorsunuz?' 
                    : 'Why do you want to delete your account?'}
                  className="form-input"
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="confirmText" style={{ color: '#cc0000', fontWeight: 'bold' }}>
                  {lang === 'tr' 
                    ? 'Onaylamak için "DELETE" yazın:' 
                    : 'Type "DELETE" to confirm:'}
                </label>
                <input
                  id="confirmText"
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="form-input"
                  style={{ 
                    fontWeight: 'bold',
                    borderColor: confirmText === 'DELETE' ? '#4CAF50' : '#ccc'
                  }}
                />
              </div>

              {deleteMessage && (
                <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                  {deleteMessage}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setConfirmText('');
                    setDeleteReason('');
                    setDeleteMessage('');
                  }}
                  className="btn-secondary"
                  disabled={isDeleting}
                >
                  {lang === 'tr' ? 'İptal' : 'Cancel'}
                </button>
                <button
                  onClick={handleDeleteAccount}
                  className="btn-danger"
                  disabled={isDeleting || confirmText !== 'DELETE'}
                  style={{
                    opacity: confirmText === 'DELETE' ? 1 : 0.5,
                    cursor: confirmText === 'DELETE' ? 'pointer' : 'not-allowed'
                  }}
                >
                  {isDeleting ? (
                    lang === 'tr' ? 'Siliniyor...' : 'Deleting...'
                  ) : (
                    <>
                      <Trash2 size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      {lang === 'tr' ? 'Hesabı Kalıcı Olarak Sil' : 'Permanently Delete Account'}
                    </>
                  )}
                </button>
              </div>

              <p style={{ 
                marginTop: '20px', 
                fontSize: '12px', 
                color: '#999',
                textAlign: 'center' 
              }}>
                {lang === 'tr'
                  ? 'Ödeme kayıtları yasal gereklilik nedeniyle 5 yıl saklanacaktır.'
                  : 'Payment records will be retained for 5 years due to legal requirements.'}
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Settings;
